import { COLLECT_REPLY_FORMATS, COLLECT_REPLY_LIMITS, UNATTACHED_TRIGGERS, WORKFLOW_LIMITS, type CollectReplyFormat } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Field, Select } from "@sagot/ui";
import { useContext } from "react";
import { IntegerInput } from "../../../workspace/shared";
import type { CollectReplyStep } from "../types";
import { StepChannelNotes } from "./channel-notes";
import { CountedTextarea } from "./counted-field";
import { DurationField } from "./duration-field";
import type { FlowContext } from "./flow-context";
import { TriggerContext } from "./hook-model";
import { PathSelect } from "./path-select";
import { REPLY_TARGETS, formatForTarget, replyProblems, replySaveOf } from "./reply-model";
import { ReplySaveField } from "./reply-save-field";
import { SwitchRow } from "./switch-row";
import { VariableList, type FormProps } from "./webhook-form";

const F = "engage.workflows.fields";
const LENGTH_ERRORS = { range: "replyRangeProblem", format: "replyFormatProblem" } as const;

/**
 * Collect customer reply: ask a question and save whatever the visitor types next. It is not a card: the chat's own
 * box takes the answer, and the answer never reaches the assistant. A bad answer is asked for once more.
 */
export function CollectReplyForm({ step, disabled, onChange, paths, canAddPath }: FormProps<CollectReplyStep> & Pick<FlowContext, "paths" | "canAddPath">) {
  const { t } = useTranslation();
  const trigger = useContext(TriggerContext);
  const fixed = formatForTarget(step.save_to);
  const timerBlocked = trigger !== undefined && UNATTACHED_TRIGGERS.has(trigger);
  const personal = replySaveOf(step.save_to).target !== "var";
  const problems = replyProblems(step);
  const setSave = (save_to: string) => {
    const wanted = formatForTarget(save_to);
    onChange({ ...step, save_to, ...(wanted ? { format: wanted } : {}) });
  };
  const setTimer = (on: boolean) => {
    if (on) onChange({ ...step, no_reply_after: { value: 30, unit: "minutes" } });
    else onChange({ ...step, no_reply_after: undefined, no_reply_goto: undefined });
  };
  return (
    <div className="space-y-5">
      <div>
        <CountedTextarea label={t(`${F}.question`)} hint={t(`${F}.replyQuestionHint`)} rows={3} value={step.text} max={WORKFLOW_LIMITS.message} disabled={disabled} onChange={(text) => { onChange({ ...step, text }); }} />
        <VariableList />
      </div>
      <ReplySaveField label={t(`${F}.saveTo`)} value={step.save_to} targets={REPLY_TARGETS} attributeTypes={["text", "number"]} fallbackKey={step.id} disabled={disabled} onChange={setSave} />
      {personal ? <p className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted">{t(`${F}.personalData`)}</p> : null}
      <Field label={t(`${F}.replyFormat`)} hint={fixed ? t(`${F}.answerTypeFixed`) : t(`${F}.replyFormatHint`)} error={problems.includes("format") ? t(`${F}.${LENGTH_ERRORS.format}`) : undefined}>
        <Select value={step.format} disabled={disabled || fixed !== undefined} onChange={(e) => { onChange({ ...step, format: e.target.value as CollectReplyFormat }); }}>
          {COLLECT_REPLY_FORMATS.map((format) => <option key={format} value={format}>{t(`${F}.replyFormats.${format}`)}</option>)}
        </Select>
      </Field>
      <fieldset className="space-y-1.5">
        <legend className="label">{t(`${F}.replyLength`)}</legend>
        <div className="flex flex-wrap items-center gap-2">
          <IntegerInput aria-label={t(`${F}.replyMin`)} className="w-24" min={1} max={COLLECT_REPLY_LIMITS.lengthMax} disabled={disabled} value={step.min_length} onChange={(min_length) => { onChange({ ...step, min_length }); }} />
          <span className="text-sm text-fg-muted" aria-hidden="true">–</span>
          <IntegerInput aria-label={t(`${F}.replyMax`)} className="w-24" min={1} max={COLLECT_REPLY_LIMITS.lengthMax} disabled={disabled} value={step.max_length} onChange={(max_length) => { onChange({ ...step, max_length }); }} />
          <span className="text-sm text-fg-muted">{t(`${F}.replyCharacters`)}</span>
        </div>
        <p className={problems.includes("range") ? "text-xs font-semibold text-danger" : "hint"} role={problems.includes("range") ? "alert" : undefined}>
          {problems.includes("range") ? t(`${F}.${LENGTH_ERRORS.range}`) : t(`${F}.replyLengthHint`, { max: COLLECT_REPLY_LIMITS.lengthMax })}
        </p>
      </fieldset>
      <PathSelect label={t(`${F}.repliedGoTo`)} hint={t(`${F}.repliedHint`)} value={step.replied_goto} paths={paths} canAddPath={canAddPath} emptyLabel={t(`${F}.nextStep`)} disabled={disabled} onChange={(replied_goto) => { onChange({ ...step, replied_goto }); }} />
      <SwitchRow
        label={t(`${F}.noReplyAfter`)}
        description={timerBlocked ? t(`${F}.noReplyUnattached`) : t(`${F}.noReplyAfterHint`)}
        checked={step.no_reply_after !== undefined}
        disabled={disabled || (timerBlocked && step.no_reply_after === undefined)}
        onChange={setTimer}
      />
      {step.no_reply_after ? (
        <>
          <DurationField legend={t(`${F}.noReplyWait`)} duration={step.no_reply_after} disabled={disabled} onChange={(no_reply_after) => { onChange({ ...step, no_reply_after }); }} />
          <PathSelect label={t(`${F}.noReplyGoTo`)} hint={t(`${F}.noReplyHint`)} value={step.no_reply_goto} paths={paths} canAddPath={canAddPath} emptyLabel={t(`${F}.endHere`)} disabled={disabled} onChange={(no_reply_goto) => { onChange({ ...step, no_reply_goto }); }} />
        </>
      ) : null}
      <StepChannelNotes type="collect_reply" />
    </div>
  );
}
